import React, { useEffect, useState } from 'react'
import { fetchBySlug, fetchCollectionList } from '../lib/cms'
import LightboxModal from './LightboxModal'

export default function CMSGallery({ slug, titleOverride, subtitle, limit = 4 }) {
  const [gallery, setGallery] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lightboxIndex, setLightboxIndex] = useState(null)

  useEffect(() => {
    let isMounted = true
    async function loadGallery() {
      setLoading(true)
      try {
        // Try direct by-slug first
        let data = await fetchBySlug(slug)
        if (!data) {
          // Fallback: search in list
          const list = await fetchCollectionList('galleri')
          data = list.find((g) => (g.slug?._ || g.slug) === slug)
        }
        if (isMounted && data) {
          setGallery(data)
        }
      } catch (err) {
        console.warn('Could not load gallery:', slug, err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    loadGallery()
    return () => {
      isMounted = false
    }
  }, [slug])

  if (loading) {
    return (
      <div className="py-12 text-center text-slate-400">
        <i className="fa-solid fa-spinner fa-spin text-2xl mb-2 text-sky-500" />
        <p className="text-sm">Henter galleri...</p>
      </div>
    )
  }

  if (!gallery || !gallery.billeder || gallery.billeder.length === 0) {
    return null
  }

  const title = titleOverride || gallery.titel || 'Billedgalleri'
  const rawImages = Array.isArray(gallery.billeder) ? gallery.billeder : [gallery.billeder]
  const images = limit ? rawImages.slice(0, limit) : rawImages

  return (
    <div className="my-10">
      {(title || subtitle) && (
        <div className="mb-6">
          <h3 className="text-xl font-bold text-slate-900 tracking-tight">{title}</h3>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
      )}

      {/* Grid of gallery images */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((imgUrl, idx) => {
          const optimized = imgUrl.includes('assets.auxodev.dk')
            ? imgUrl.replace('assets.auxodev.dk/', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/')
            : imgUrl

          return (
            <button
              key={idx}
              type="button"
              onClick={() => setLightboxIndex(idx)}
              className="group relative aspect-4/3 overflow-hidden rounded-none bg-slate-100 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 cursor-pointer"
              aria-label={`Åbn billede ${idx + 1} i stor visning`}
            >
              <img
                src={optimized}
                alt={`${gallery.titel} - billede ${idx + 1}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/40 transition-colors flex items-center justify-center">
                <span className="text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md">
                  <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <LightboxModal
          images={images}
          activeIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}
    </div>
  )
}
